// LLM180 - Analysis Run detail page at /analysis-runs/:runId
//
// Single-screen "view this run end-to-end" surface. Lives between the Hub
// (list view) and the wizard (configure-and-edit view). Used as the canonical
// destination for terminal runs (delivered / failed) where there's nothing
// to configure - you just want to inspect the deliverables.
//
// Sections:
//   1. Header  - brand, title, status, auto-advance chip, assignee, validator
//   2. Stage breadcrumb (read-only)
//   3. Action row  - Resume (non-terminal), Open in wizard, Chat with MAD Pilot,
//      Open report, Download PPTX, Abort, Delete
//   4. Metadata card - created/started/delivered, cost, aliases preflight
//   5. Audit matrix (embedded)
//   6. PPTX latest artifact (multi-version is LLM179)

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import AppShell from "../components/AppShell";
import AuditMatrix from "../components/AuditMatrix";
import Toast from "../components/Toast";
import "../components/toast.css";
import {
  analysisRunGet,
  analysisRunAbort,
  analysisRunSoftDelete,
  analysisRunPptxLatest,
  analysisRunPptxDownload,
  analysisRunPptxArtifacts,
  analysisRunPptxVersionDownload,
  fetchAuditMatrix,
  fetchBrands,
} from "../services/api";

const NAVY = "#0a0f24";
const GOLD = "#ffcc33";

const STATUS_COLORS = {
  pending: { bg: "#f3f4f6", fg: "#374151" },
  running_audit: { bg: "#fef3c7", fg: "#92400e" },
  audit_complete: { bg: "#dbeafe", fg: "#1e40af" },
  running_analysis: { bg: "#fef3c7", fg: "#92400e" },
  analysis_complete: { bg: "#dbeafe", fg: "#1e40af" },
  generating_pptx: { bg: "#fef3c7", fg: "#92400e" },
  ready: { bg: "#d1fae5", fg: "#065f46" },
  delivered: { bg: "#dcfce7", fg: "#166534" },
  failed: { bg: "#fee2e2", fg: "#991b1b" },
};

const WIZARD_STAGES = [
  { key: 1, label: "Configure", statuses: ["pending"] },
  { key: 2, label: "Audit", statuses: ["running_audit", "audit_complete"] },
  { key: 3, label: "Analysis", statuses: ["running_analysis", "analysis_complete"] },
  { key: 4, label: "Deliver", statuses: ["generating_pptx", "ready", "delivered"] },
];

const TERMINAL_STATUSES = new Set(["delivered", "failed"]);

function stageForStatus(status) {
  for (const s of WIZARD_STAGES) {
    if (s.statuses.includes(status)) return s.key;
  }
  return 1;
}

function fmtDateTime(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleString();
}

export default function AnalysisRunDetailPage({ currentUser }) {
  const { runId: runIdParam } = useParams();
  const runId = Number(runIdParam);
  const navigate = useNavigate();

  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [latestPptx, setLatestPptx] = useState(null);
  const [pptxVersions, setPptxVersions] = useState([]); // newest first
  const [pptxExpanded, setPptxExpanded] = useState(false);
  const [brandName, setBrandName] = useState(null);
  const [toast, setToast] = useState(null);
  const [acting, setActing] = useState(null);

  const showToast = (message, type = "info") => setToast({ message, type });

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await analysisRunGet(runId);
      setRun(r);
    } catch (e) {
      setError(e?.message || "Run not found");
      setRun(null);
    } finally {
      setLoading(false);
    }
  }, [runId]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Resolve brand name once
  useEffect(() => {
    if (!run?.brand_id) return;
    fetchBrands()
      .then((list) => {
        const arr = Array.isArray(list) ? list : list?.brands || [];
        const b = arr.find((x) => Number(x.id) === Number(run.brand_id));
        setBrandName(b?.name || null);
      })
      .catch(() => setBrandName(null));
  }, [run?.brand_id]);

  // Lazy-load matrix when audit_id is known
  useEffect(() => {
    if (!run?.audit_id) return;
    setMatrixLoading(true);
    fetchAuditMatrix(run.audit_id)
      .then(setMatrix)
      .catch(() => setMatrix(null))
      .finally(() => setMatrixLoading(false));
  }, [run?.audit_id]);

  // Load latest PPTX artifact + version history
  useEffect(() => {
    if (!run?.id) return;
    analysisRunPptxLatest(run.id)
      .then(setLatestPptx)
      .catch(() => setLatestPptx(null));
    analysisRunPptxArtifacts(run.id)
      .then((res) => setPptxVersions(res?.items || []))
      .catch(() => setPptxVersions([]));
  }, [run?.id]);

  const stage = useMemo(() => (run ? stageForStatus(run.status) : 1), [run]);
  const terminal = run && TERMINAL_STATUSES.has(run.status);

  const onResume = () => {
    navigate(`/brands/${run.brand_id}/new-analysis?runId=${run.id}`);
  };

  const onOpenChat = () => {
    if (!run.copilot_session_id) return;
    navigate(`/analysis/${run.copilot_session_id}`);
  };

  const onAbort = async () => {
    if (!run || terminal) return;
    if (
      !window.confirm(
        `Abort this run? It will be marked failed and any in-flight model runs will be cancelled.`
      )
    ) {
      return;
    }
    setActing("abort");
    try {
      await analysisRunAbort(run.id);
      showToast("Run aborted", "success");
      await reload();
    } catch (e) {
      showToast(e?.message || "Abort failed", "error");
    } finally {
      setActing(null);
    }
  };

  const onDelete = async () => {
    if (!run) return;
    if (!window.confirm("Soft-delete this run? It will be hidden from the hub; data is preserved.")) return;
    setActing("delete");
    try {
      await analysisRunSoftDelete(run.id);
      showToast("Run deleted", "success");
      navigate("/analyses");
    } catch (e) {
      showToast(e?.message || "Delete failed", "error");
      setActing(null);
    }
  };

  const onDownloadPptx = async () => {
    if (!run) return;
    setActing("pptx");
    try {
      const { blob, filename } = await analysisRunPptxDownload(run.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || `MAD_Analysis_${run.id}.pptx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      showToast(e?.message || "Download failed", "error");
    } finally {
      setActing(null);
    }
  };

  // LLM179: download a specific historical PPTX version (not just latest)
  const onDownloadVersion = async (versionNo) => {
    if (!run) return;
    setActing(`pptx-v${versionNo}`);
    try {
      const { blob, filename } = await analysisRunPptxVersionDownload(run.id, versionNo);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || `MAD_Analysis_${run.id}_v${versionNo}.pptx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      showToast(e?.message || "Download failed", "error");
    } finally {
      setActing(null);
    }
  };

  if (loading) {
    return (
      <AppShell currentUser={currentUser} headerSubtitle="Analysis Run">
        <div style={{ padding: 40, textAlign: "center", color: "#6b7280" }}>Loading…</div>
      </AppShell>
    );
  }

  if (error || !run) {
    return (
      <AppShell currentUser={currentUser} headerSubtitle="Analysis Run">
        <div style={{ padding: 40, textAlign: "center", color: "#b91c1c" }}>
          {error || "Run not found."}
          <div style={{ marginTop: 14 }}>
            <button onClick={() => navigate("/analyses")} className="btn-secondary">
              Back to Analyses
            </button>
          </div>
        </div>
      </AppShell>
    );
  }

  const statusColor = STATUS_COLORS[run.status] || { bg: "#f3f4f6", fg: "#374151" };

  return (
    <AppShell currentUser={currentUser} headerSubtitle="Analysis Run">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Top header card */}
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          padding: "16px 18px",
          border: "1px solid #e5e7eb",
          marginBottom: 14,
          boxShadow: "0 4px 16px rgba(15,23,42,0.06)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              {brandName || `Brand #${run.brand_id}`} · Run #{run.id}
            </div>
            <div style={{ fontSize: 22, fontWeight: 900, color: NAVY, marginTop: 4 }}>
              {run.title || `Run #${run.id}`}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
              <span
                style={{
                  padding: "3px 10px",
                  borderRadius: 999,
                  background: statusColor.bg,
                  color: statusColor.fg,
                  fontSize: 11,
                  fontWeight: 900,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                {run.status.replace(/_/g, " ")}
              </span>
              {run.validator_status && (
                <span
                  title={`Quality: ${run.validator_status === "green" ? "Passed" : run.validator_status === "yellow" ? "Warnings" : "Issues"}`}
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 999,
                    background:
                      run.validator_status === "green" ? "#10b981"
                        : run.validator_status === "yellow" ? "#d4a017"
                          : "#dc2626",
                  }}
                />
              )}
              {run.auto_advance && (
                <span
                  title="End-to-end automation enabled"
                  style={{
                    padding: "2px 8px",
                    borderRadius: 999,
                    background: "#eff6ff",
                    color: "#1e3a8a",
                    fontSize: 10,
                    fontWeight: 800,
                    border: "1px solid #bfdbfe",
                  }}
                >
                  ⚡ Auto
                </span>
              )}
              {run.revision_count > 0 && (
                <span
                  title={`Revised ${run.revision_count} time${run.revision_count === 1 ? "" : "s"}`}
                  style={{
                    padding: "2px 8px",
                    borderRadius: 999,
                    background: "#fef3c7",
                    color: "#92400e",
                    fontSize: 10,
                    fontWeight: 800,
                    border: "1px solid #fde68a",
                  }}
                >
                  ↻ v{run.revision_count + 1}
                </span>
              )}
              {run.assignee && (
                <span style={{ fontSize: 11, color: "#6b7280" }}>
                  Assigned to <strong style={{ color: "#1e3a8a" }}>{run.assignee.full_name || run.assignee.email}</strong>
                </span>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={() => navigate("/analyses")}
            className="btn-secondary"
            style={{ padding: "8px 14px", borderRadius: 10, fontWeight: 700 }}
          >
            ← Back to Analyses
          </button>
        </div>

        {/* Stage breadcrumb */}
        <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 14, flexWrap: "wrap" }}>
          {WIZARD_STAGES.map((s, i) => {
            const done = i < stage - 1;
            const active = i === stage - 1;
            return (
              <React.Fragment key={s.key}>
                <div
                  style={{
                    padding: "3px 10px",
                    borderRadius: 6,
                    fontSize: 11,
                    fontWeight: active ? 900 : 700,
                    background: active ? NAVY : done ? "#e5e7eb" : "#f9fafb",
                    color: active ? GOLD : done ? "#374151" : "#9ca3af",
                    border: active ? `1px solid ${GOLD}` : "1px solid #e5e7eb",
                  }}
                >
                  {s.key}. {s.label}
                </div>
                {i < WIZARD_STAGES.length - 1 && (
                  <span style={{ color: done ? GOLD : "#d1d5db", fontSize: 12 }}>›</span>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Action row */}
      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          marginBottom: 14,
        }}
      >
        {!terminal && (
          <button onClick={onResume} className="btn-primary" style={{ padding: "8px 14px", borderRadius: 10, fontWeight: 800 }}>
            Resume in wizard →
          </button>
        )}
        {terminal && (
          <button onClick={onResume} className="btn-secondary" style={{ padding: "8px 14px", borderRadius: 10, fontWeight: 700 }}>
            Open in wizard
          </button>
        )}
        {run.copilot_session_id && (
          <button
            onClick={onOpenChat}
            className="btn-secondary"
            style={{ padding: "8px 14px", borderRadius: 10, fontWeight: 700 }}
          >
            View analysis report
          </button>
        )}
        {latestPptx && (
          <button
            onClick={onDownloadPptx}
            disabled={acting === "pptx"}
            className="btn-secondary"
            style={{ padding: "8px 14px", borderRadius: 10, fontWeight: 700 }}
            title={`PPTX v${latestPptx.version_no}`}
          >
            {acting === "pptx" ? "Downloading…" : `Download PPTX (v${latestPptx.version_no})`}
          </button>
        )}
        <div style={{ flex: 1 }} />
        {!terminal && (
          <button
            onClick={onAbort}
            disabled={acting === "abort"}
            style={{ padding: "8px 14px", borderRadius: 10, fontWeight: 700, border: "1px solid #fecaca", background: "#fff", color: "#b91c1c", cursor: "pointer" }}
          >
            {acting === "abort" ? "Aborting…" : "Abort run"}
          </button>
        )}
        <button
          onClick={onDelete}
          disabled={acting === "delete"}
          style={{ padding: "8px 14px", borderRadius: 10, fontWeight: 700, border: "1px solid #e5e7eb", background: "#fff", color: "#6b7280", cursor: "pointer" }}
        >
          {acting === "delete" ? "Deleting…" : "Delete"}
        </button>
      </div>

      {/* Metadata grid */}
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          padding: 16,
          border: "1px solid #e5e7eb",
          marginBottom: 14,
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 800, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 10 }}>
          Metadata
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
          <Meta label="Created" value={fmtDateTime(run.created_at)} />
          <Meta label="Audit started" value={fmtDateTime(run.audit_started_at)} />
          <Meta label="Analysis started" value={fmtDateTime(run.analysis_started_at)} />
          <Meta label="Delivered" value={fmtDateTime(run.delivered_at)} />
          <Meta
            label="Cost (estimated)"
            value={run.estimated_cost_usd != null ? `$${Number(run.estimated_cost_usd).toFixed(3)}` : "-"}
          />
          <Meta
            label="Cost (actual)"
            value={run.actual_cost_usd != null ? `$${Number(run.actual_cost_usd).toFixed(3)}` : "-"}
          />
          <Meta
            label="Models"
            value={(run.model_overrides || []).join(", ") || "-"}
          />
          <Meta
            label="Location"
            value={run.use_location ? `${run.location_country || ""}${run.location_city ? `, ${run.location_city}` : ""}` || "-" : "-"}
          />
        </div>

        {run.aliases_pre_flight_json && (
          <div style={{ marginTop: 14, padding: 12, borderRadius: 10, background: "#f9fafb", border: "1px solid #e5e7eb" }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: "#6b7280", textTransform: "uppercase" }}>
              Aliases pre-flight
            </div>
            <div style={{ fontSize: 12, color: "#374151", marginTop: 4 }}>
              <strong>Accepted:</strong>{" "}
              {(run.aliases_pre_flight_json.accepted || []).join(", ") || "(none)"}
              {" · "}
              <strong>Rejected:</strong>{" "}
              {(run.aliases_pre_flight_json.rejected || []).join(", ") || "(none)"}
            </div>
          </div>
        )}
      </div>

      {/* Audit matrix */}
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          padding: 16,
          border: "1px solid #e5e7eb",
          marginBottom: 14,
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 800, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 10 }}>
          Audit matrix
        </div>
        {!run.audit_id ? (
          <div style={{ padding: 20, color: "#9ca3af", fontSize: 13, textAlign: "center" }}>
            No audit yet - this run hasn't been started.
          </div>
        ) : matrixLoading ? (
          <div style={{ padding: 20, color: "#6b7280", fontSize: 13, textAlign: "center" }}>
            Loading matrix…
          </div>
        ) : !matrix || (matrix?.matrix?.rows || []).length === 0 ? (
          <div style={{ padding: 20, color: "#9ca3af", fontSize: 13, textAlign: "center" }}>
            Matrix is empty.
          </div>
        ) : (
          <AuditMatrix
            audit={matrix.audit}
            matrix={matrix.matrix}
            onRefresh={async () => {
              try {
                const m = await fetchAuditMatrix(run.audit_id);
                setMatrix(m);
              } catch (e) {
                showToast(e?.message || "Failed to refresh matrix", "error");
              }
            }}
          />
        )}
      </div>

      {/* PPTX with version history (LLM179) */}
      {latestPptx && (
        <div
          style={{
            background: "#fff",
            borderRadius: 14,
            padding: 16,
            border: "1px solid #e5e7eb",
            marginBottom: 14,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 800, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 10 }}>
            Deliverable
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
            <div style={{ fontSize: 13, color: "#374151" }}>
              <strong>Latest PPTX:</strong> v{latestPptx.version_no}
              <span style={{ color: "#6b7280", marginLeft: 8 }}>
                · generated {fmtDateTime(latestPptx.generated_at)}
              </span>
              {pptxVersions.length > 1 && (
                <button
                  type="button"
                  onClick={() => setPptxExpanded((v) => !v)}
                  style={{
                    marginLeft: 12,
                    background: "none",
                    border: "none",
                    color: "#1e3a8a",
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: "pointer",
                    textDecoration: "underline dotted",
                    padding: 0,
                  }}
                >
                  {pptxExpanded
                    ? "Hide history"
                    : `${pptxVersions.length - 1} older version${pptxVersions.length === 2 ? "" : "s"}`}
                </button>
              )}
            </div>
            <button
              onClick={onDownloadPptx}
              disabled={acting === "pptx"}
              className="btn-primary"
              style={{ padding: "8px 14px", borderRadius: 10, fontWeight: 800 }}
            >
              {acting === "pptx" ? "Downloading…" : "Download latest"}
            </button>
          </div>

          {pptxExpanded && pptxVersions.length > 0 && (
            <div style={{ marginTop: 14, borderTop: "1px solid #f3f4f6", paddingTop: 12 }}>
              <div style={{ display: "grid", gridTemplateColumns: "auto 1fr auto auto auto", rowGap: 8, columnGap: 12, alignItems: "center", fontSize: 12 }}>
                <div style={{ fontWeight: 800, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>Version</div>
                <div style={{ fontWeight: 800, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>Notes</div>
                <div style={{ fontWeight: 800, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>Generated</div>
                <div style={{ fontWeight: 800, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>Size</div>
                <div />
                {pptxVersions.map((v) => (
                  <React.Fragment key={v.id}>
                    <div style={{ fontWeight: 800, color: "#111827" }}>
                      v{v.version_no}
                      {latestPptx?.version_no === v.version_no && (
                        <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, color: "#166534" }}>· latest</span>
                      )}
                    </div>
                    <div style={{ color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {v.notes || "-"}
                    </div>
                    <div style={{ color: "#6b7280", fontVariantNumeric: "tabular-nums" }}>
                      {fmtDateTime(v.generated_at)}
                    </div>
                    <div style={{ color: "#6b7280", fontVariantNumeric: "tabular-nums" }}>
                      {v.file_size_bytes != null ? `${(Number(v.file_size_bytes) / 1024).toFixed(1)} KB` : "-"}
                    </div>
                    <button
                      onClick={() => onDownloadVersion(v.version_no)}
                      disabled={acting === `pptx-v${v.version_no}`}
                      className="btn-secondary"
                      style={{ padding: "4px 10px", fontSize: 11, borderRadius: 8, fontWeight: 700 }}
                    >
                      {acting === `pptx-v${v.version_no}` ? "…" : "Download"}
                    </button>
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </AppShell>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 800, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em" }}>
        {label}
      </div>
      <div style={{ fontSize: 13, color: "#111827", marginTop: 2, fontWeight: 600 }}>{value}</div>
    </div>
  );
}
